<!--
  =====================================================================
  ACTIVITIES DETAIL PAGE - ACTIVITY SPECIFICATION VIEW
  =====================================================================
  
  Displays detailed activity information with comprehensive content sections.
  
  FEATURES:
  - Dynamic activity lookup from URL parameters (macro-area/category/activity)
  - Multi-image carousel with smart placeholder detection
  - Responsive grid layout with multiple section types
  - Back navigation to parent category
  - Dynamic category name resolution
  - Handles both activity store data and category card data
  - Carousel resets on activity change
  - Back button visibility management
  - Multiple content section types (center, tables, lists, grids, etc.)
  - Shows loading/error/not-found states
  
  URL PATTERNS:
  - /fondazione/attivita/:macroArea/:category/:activity
  - Direct access pages at /fondazione/attivita/links/direct-access/:activity
  
  =====================================================================
-->

<template>
  <div>
    <SiteHeader />

    <main class="activity-detail">
      <div class="activity-back-top" v-show="!isMegamenuOpen && !isFooterButtonVisible && !isDirectAccessPage">
        <a :href="categoryBackLink" class="back-to-category">
          <span class="arrow">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span class="label">{{ categoryBackText }}</span>
        </a>
      </div>

      <div v-if="state.loading" class="loading-state">
        <div class="dots-loader">
          <span class="dot dot-1"></span>
          <span class="dot dot-2"></span>
          <span class="dot dot-3"></span>
        </div>
      </div>
      
      <div v-else-if="state.error" class="error-state">Errore nel caricamento</div>
      
      <div v-else-if="!activityFound">
        <div class="activity-not-found">
          <h1>Attività non trovata</h1>
          <p>L'attività che stai cercando non esiste o è stata rimossa.</p>
          <a :href="categoryBackLink" class="back-to-category">
            <span class="arrow">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="label">{{ categoryBackText }}</span>
          </a>
        </div>
      </div>
      
      <div v-else>
        <div class="activity-header">
          <p class="activity-eyebrow" v-if="activityEyebrow">{{ activityEyebrow }}</p>
          <h1 class="activity-title">{{ activityTitle }}</h1>
        </div>

        <div v-if="shouldShowCarousel" class="activity-image-carousel">
          <div v-if="isMultipleImages" class="carousel-container">
            <button 
              v-show="carouselIndex > 0"
              class="carousel-arrow carousel-arrow-left" 
              @click="carouselIndex--"
              aria-label="Immagine precedente"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <div class="carousel-image-wrapper">
              <img 
                :src="currentCarouselImage" 
                :alt="activityTitle" 
                class="carousel-image"
              />
            </div>

            <button 
              v-show="carouselIndex < productImages.length - 1"
              class="carousel-arrow carousel-arrow-right" 
              @click="carouselIndex++"
              aria-label="Immagine successiva"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div v-else class="single-image-wrapper">
            <img 
              :src="currentCarouselImage" 
              :alt="activityTitle" 
              class="carousel-image"
            />
          </div>

          <div v-if="isMultipleImages" class="carousel-dots">
            <button
              v-for="(_, index) in activityImages"
              :key="index"
              class="dot"
              :class="{ active: carouselIndex === index }"
              @click="carouselIndex = index"
              :aria-label="`Immagine ${index + 1}`"
            />
          </div>
        </div>

        <component
          v-for="(section, index) in enhancedSections"
          :key="index"
          :is="getComponent(section.type)"
          :content="section.content"
        >
          <template v-if="index === pdfSectionIndex" #after-text>
            <div class="activity-pdf">
              <PdfBlock :src="activityPdf" :title="activityTitle" fit-sheet />
            </div>
          </template>
        </component>

        <div v-if="activityPdf && pdfSectionIndex === -1" class="activity-pdf">
          <PdfBlock :src="activityPdf" :title="activityTitle" fit-sheet />
        </div>
      </div>
    </main>

    <ArrowUp />
    <SiteFooter />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed, watch } from 'vue'
import { state, loadPageContent } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// SHARED LAYOUT COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SiteHeader from '@/components/sections/shared/SiteHeader.vue'
import SiteFooter from '@/components/sections/shared/SiteFooter.vue'

// ───────────────────────────────────────────────────────────────────
// HTML-BASED SECTION COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SectionDocumentHtml from '@/components/sections/html/SectionDocumentHtml.vue'
import HtmlTextSection from '@/components/sections/html/SectionHtmlText.vue'
import PdfBlock from '@/components/sections/shared/PdfBlock.vue'
import ArrowUp from '@/components/ArrowUp.vue'

// ───────────────────────────────────────────────────────────────────
// COMPONENT STATE
// ───────────────────────────────────────────────────────────────────

const activityData = ref(null)
const macroArea = ref(null)
const category = ref(null)
const activity = ref(null)
const isMegamenuOpen = ref(false)
const isFooterButtonVisible = ref(false)
const carouselIndex = ref(0)

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES - DATA RESOLUTION
// ───────────────────────────────────────────────────────────────────

/**
 * Enhanced page sections from activity data
 * @returns {Array} Sections array from activity store
 */
const enhancedSections = computed(() => {
  if (!activityData.value?.sections) return []
  return [...activityData.value.sections]
})

/**
 * Determines if activity was successfully found
 * @returns {boolean} True if activity has title
 */
const activityFound = computed(() => {
  const found = !!activityData.value?.title
  return found
})

/**
 * Generates back link to parent category
 * Falls back to main activities page if no category context
 * @returns {string} Category URL or activities home
 */
const categoryBackLink = computed(() => {
  if (macroArea.value && category.value) {
    return `/fondazione/attivita/${macroArea.value}/${category.value}`
  }
  return '/fondazione/attivita'
})

/**
 * Resolves back button text from category metadata
 * @returns {string} Contextual back button text
 */
const categoryBackText = computed(() => {
  if (state.categorie && macroArea.value && category.value) {
    const categoryData = state.categorie[macroArea.value]?.[category.value]
    return categoryData?.title ? `Torna a ${categoryData.title}` : 'Torna alla categoria'
  }
  return 'Torna alle attività'
})

/**
 * Gets activity title, defaults to generic label
 * @returns {string} Activity title
 */
const activityTitle = computed(() => activityData.value?.title || 'Attività')

/**
 * Resolves activity eyebrow (subheading) from category or field
 * @returns {string} Eyebrow text or category name
 */
const activityEyebrow = computed(() => {
  if (activityData.value?.eyebrow) return activityData.value.eyebrow
  const cat = categoryBackText.value
  return cat ? cat.replace('Torna a ', '').replace('Torna alle ', '') : ''
})

/**
 * Identifies if page is direct access page (no back navigation)
 * @returns {boolean} True if in links/direct-access category
 */
const isDirectAccessPage = computed(() => {
  return macroArea.value === 'links' && category.value === 'direct-access'
})

// ───────────────────────────────────────────────────────────────────
// PLACEHOLDER IMAGE DETECTION
// ───────────────────────────────────────────────────────────────────

/**
 * Extracts filename from URL (handles various URL formats)
 * @param {string} url - The image URL
 * @returns {string} Filename without path
 */
function getBasename(url) {
  if (!url) return ''
  const parts = url.split('/')
  return parts[parts.length - 1].split('?')[0]
}

/**
 * Detects if image is a placeholder (generic template image)
 * Matches: placeholder_news, placeholder_product, placeholder_document, etc.
 * 
 * @param {string} src - Image source URL
 * @returns {boolean} True if image is identified as placeholder
 */
function isPlaceholderImage(src) {
  if (!src) return false
  const name = getBasename(src)
  const placeholderRegex = /^placeholder[_-]?(?:news|product|document)(?:_\d+)?\.(png|jpg|jpeg|webp)$/i
  return placeholderRegex.test(name)
}

// ───────────────────────────────────────────────────────────────────
// CAROUSEL IMAGE LOGIC
// ───────────────────────────────────────────────────────────────────

/**
 * Gets normalized activity images as array
 * Converts single image to array format for uniform handling
 * @returns {Array} Array of image URLs
 */
const activityImages = computed(() => {
  if (!activityData.value?.image) return []
  const images = Array.isArray(activityData.value.image) 
    ? activityData.value.image 
    : [activityData.value.image]
  return images
})

/**
 * Determines if carousel should display multiple image mode
 * @returns {boolean} True if 2+ images available
 */
const isMultipleImages = computed(() => {
  return Array.isArray(activityImages.value) && activityImages.value.length > 1
})

/**
 * Smart carousel visibility control
 * Hides carousel if no valid (non-placeholder) images exist
 * @returns {boolean} True if carousel should display
 */
const shouldShowCarousel = computed(() => {
  if (!Array.isArray(activityImages.value) || activityImages.value.length === 0) {
    return false
  }
  const allPlaceholders = activityImages.value.every(img => isPlaceholderImage(img))
  return !allPlaceholders
})

/**
 * Gets current carousel image at navigation index
 * @returns {string|null} Current image URL or null
 */
const currentCarouselImage = computed(() => {
  if (!Array.isArray(activityImages.value) || activityImages.value.length === 0) return null
  return activityImages.value[carouselIndex.value] || null
})

// ───────────────────────────────────────────────────────────────────
// LIBRO CONSULTABILE (PDF)
// ───────────────────────────────────────────────────────────────────

/**
 * Percorso del PDF consultabile dell'attivita'
 *
 * Il campo `pdf` sta sulla card, accanto a `image`: e' gia' previsto dal CMS su
 * ogni attivita' (arriva valorizzato a stringa vuota) e finora nessuno lo
 * leggeva. Vale lo stesso di opere d'arte: se c'e' un pdf, compare il lettore.
 *
 * A differenza di opere d'arte il pdf NON sostituisce le immagini: qui il
 * carosello ha un'area sua in cima alla pagina, quindi il libro si aggiunge
 * sotto il testo invece di rimpiazzarle (vedi `pdfSectionIndex`).
 *
 * @returns {string} Percorso del pdf, stringa vuota se l'attivita' non ne ha
 */
const activityPdf = computed(() => {
  const pdf = activityData.value?.pdf
  return typeof pdf === 'string' ? pdf.trim() : ''
})

/**
 * Indice della sezione che ospita il lettore PDF
 *
 * Il lettore va fra il testo e gli elenchi di documenti e link, che stanno
 * dentro la stessa sezione `html-text`: per questo entra nel suo slot
 * `after-text` invece di stare dopo tutte le sezioni.
 *
 * GARANTISCE: l'indice della prima sezione `html-text` se l'attivita' ha un pdf.
 * NON GARANTISCE: una posizione fra testo ed elenchi quando la pagina non ha una
 * sezione `html-text` (-1): li' il lettore resta in fondo, come prima.
 *
 * @returns {number} Indice della sezione, -1 se non c'e' pdf o sezione adatta
 */
const pdfSectionIndex = computed(() => {
  if (!activityPdf.value) return -1
  return enhancedSections.value.findIndex(section => section.type === 'html-text')
})

// ───────────────────────────────────────────────────────────────────
// COMPONENT RESOLVER
// ───────────────────────────────────────────────────────────────────

/**
 * Maps content section types to Vue components
 * Supports both structured and HTML-based sections
 * 
 * @param {string} type - Section type identifier
 * @returns {VueComponent} Component to render section
 */
function getComponent(type) {
  switch (type) {

    case 'section-document-html':   return SectionDocumentHtml
    case 'html-text':               return HtmlTextSection
  }
}

// ───────────────────────────────────────────────────────────────────
// CAROUSEL STATE MANAGEMENT
// ───────────────────────────────────────────────────────────────────

/**
 * Reset carousel index when activity changes
 * Ensures fresh carousel state for each activity view
 */
watch(activityData, () => {
  carouselIndex.value = 0
})

// ───────────────────────────────────────────────────────────────────
// BACK BUTTON VISIBILITY MANAGEMENT
// ───────────────────────────────────────────────────────────────────

/**
 * Checks megamenu and footer visibility to control back button display
 * Hides top back button if footer has visible back button
 */
const checkMegamenuAndFooter = () => {
  const megamenu = document.querySelector('.mega-menu')
  isMegamenuOpen.value = !!megamenu
  
  const footerButton = document.querySelector('footer .back-to-category')
  if (footerButton) {
    const rect = footerButton.getBoundingClientRect()
    isFooterButtonVisible.value = rect.top >= 0 && rect.top <= window.innerHeight
  }
}

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Initialize page on component mount
 * - Parse URL for activity location (macro-area/category/activity)
 * - Load activity data from store
 * - Normalize image arrays
 * - Setup DOM observers and event listeners
 */
onMounted(async () => {
  // Parse URL: /fondazione/attivita/:macroArea/:category/:activity
  const pathSegments = window.location.pathname.split('/').filter(Boolean)
  
  let segments = pathSegments
  if (segments[0] === 'fondazione') {
    segments = segments.slice(1)
  }
  
  if (segments[0] === 'attivita') {
    segments = segments.slice(1)
  }
  
  macroArea.value = segments[0]   // e.g. 'links'
  category.value = segments[1]    // e.g. 'direct-access'
  activity.value = segments[2]     // e.g. 'esempio_VOox6S'
  
  // Load activity index if not already loaded
  if (!state.attivita || Object.keys(state.attivita).length === 0) {
    await loadPageContent('attivita')
  }
  
  // Try to find activity in store (by flat key first, then by simple key)
  const flatKey = `${macroArea.value}/${category.value}/${activity.value}`
  activityData.value = state.attivita?.[flatKey] || state.attivita?.[activity.value]

  // Fallback: search in category cards
  if (!activityData.value && state.categorie) {
    const categoryObj = state.categorie[macroArea.value]?.[category.value]
    if (categoryObj && categoryObj.cards) {
      const card = categoryObj.cards.find(c => c.slug === activity.value)
      if (card) {
        activityData.value = {
          ...card,
          // Ensure image is always array
          image: card.image ? (Array.isArray(card.image) ? card.image : [card.image]) : null
        }
      }
    }
  }
  
  // Normalize image array for found activity
  if (activityData.value && activityData.value.image) {
    if (!Array.isArray(activityData.value.image)) {
      activityData.value.image = [activityData.value.image]
    }
  }

  // Setup DOM observer for visibility tracking
  const observer = new MutationObserver(checkMegamenuAndFooter)
  observer.observe(document.body, { 
    childList: true,
    attributes: true, 
    attributeFilter: ['class'],
    subtree: true 
  })
  
  // Setup scroll/resize event listeners
  window.addEventListener('scroll', checkMegamenuAndFooter)
  window.addEventListener('resize', checkMegamenuAndFooter)
  setTimeout(checkMegamenuAndFooter, 100)

  // Cleanup on unmount
  onUnmounted(() => {
    observer.disconnect()
    window.removeEventListener('scroll', checkMegamenuAndFooter)
    window.removeEventListener('resize', checkMegamenuAndFooter)
  })
})
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";
@import "@/assets/styles/partials/compat";

/* ------------------------------------------------------------------
   Wrapper
------------------------------------------------------------------ */
.activity-detail {
  padding: $space-2 $container-padding-x;
  max-width: 1200px;
  margin: 0 auto;
  color: $clr-brand-gray;
}

/* ------------------------------------------------------------------
   Bottone torna indietro
------------------------------------------------------------------ */
.activity-back-top {
  position: fixed;
  top: calc(var(--header-total-height) + 1.2rem);
  left: 1rem;
  z-index: 150;

  .back-to-category {
    font-size: clamp(0.95rem, 2.5vw, 1.15rem);
    font-weight: 600;
    color: $clr-brand-dark;
    text-decoration: none;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    @include flex-gap(0.35rem);
    line-height: 1;
    padding: 0.6rem 0;

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;
        transform: scale(1.05);

        .arrow {
          transform: rotate(-90deg);
        }
      }
    }

    .label {
      display: inline-block;
      white-space: nowrap;
      transition: opacity 0.25s ease, transform 0.25s ease;
      will-change: opacity, transform;
    }

    @media (max-width: 1700px) {
      @include flex-gap(0);

      .label {
        opacity: 0;
        transform: translateX(-6px);
        pointer-events: none;
      }
    }
  }

  @media (max-width: 1300px) {
    top: calc(var(--header-total-height) + 1.2rem);
    left: 1rem;
  }

  @media (max-width: 920px) { 
    display: none; 
  }

  @media (max-width: 768px) {
    left: 0;
  }
}

/* ------------------------------------------------------------------
   Header (SENZA IMMAGINE)
------------------------------------------------------------------ */
.activity-header {
  margin-bottom: $space-3;

  .activity-eyebrow {
    position: relative;
    display: inline-block;
    font-size: $font-eyebrow;
    font-weight: 600;
    font-family: 'Karla', sans-serif!important;
    color: $clr-tertiary;
    text-transform: uppercase;
    letter-spacing: 1px;
    line-height: 1.2;
    margin: 0 0 $space-2 0;
    padding-left: $space-1-5;
    cursor: default;
    white-space: nowrap;
    transition: all 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: -30px;
      width: 40%;
      height: 12px;
      background-color: rgba($clr-tertiary, 0.7);
      transition: all 0.5s ease;
    }

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;

        &::before {
          background: rgba($clr-tertiary, 0.8);
          width: 120%;
          transform: scaleY(1.2);
        }
      }
    }
  }

  .activity-title {
    font-size: $font-title;
    font-weight: 300;
    font-family: 'Karla', sans-serif!important;
    color: $clr-brand-gray;
    line-height: 1.2;
    margin: 0 0 $space-0-25 0;
  }
}

/* Freccia */
.arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1em;
  height: 1em;
  transition: transform 0.3s ease;
  transform: rotate(90deg);

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
}

/* ------------------------------------------------------------------
   Loading / Error
------------------------------------------------------------------ */
.loading-state,
.error-state {
  text-align: center;
  padding: $space-4 $space-1;
  color: $clr-text-light;
  font-size: $font-base;
}

@media (max-width: 768px) {
  .loading-state,
  .error-state {
    padding: $space-3 $space-0-5;
    font-size: $font-base-sm;
  }
}

/* ------------------------------------------------------------------
   Not found
------------------------------------------------------------------ */
.activity-not-found {
  text-align: center;
  padding: $space-4 $space-1;

  h1 {
    font-size: $font-title;
    color: $clr-brand-dark;
    margin-bottom: $space-1;
  }

  p {
    font-size: $font-base;
    color: $clr-text-light;
    margin-bottom: $space-2;
  }

  .back-to-category {
    display: inline-flex;
    align-items: center;
    @include flex-gap(0.35rem);
    line-height: 1;
    font-size: clamp(0.95rem, 2.5vw, 1.15rem);
    font-weight: 600;
    color: $clr-brand-dark;
    text-decoration: none;
    transition: all 0.25s ease;
    padding: 0.6rem 0;

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;
        transform: translateX(-4px);
      }
    }
  }
}

/* ------------------------------------------------------------------
   Media queries - Display medio-grande intermedio (1025px – 1300px)
   Necessario perché main { padding: 0 !important } nel global
   sovrascrive il padding del componente; qui usiamo !important con
   specificità maggiore (selettore di classe) per ripristinarlo.
------------------------------------------------------------------ */
@media (min-width: 1025px) and (max-width: 1300px) {
  .activity-detail {
    padding-left: clamp(2rem, 4vw, 3rem) !important;
    padding-right: clamp(2rem, 4vw, 3rem) !important;
  }
}

/* ------------------------------------------------------------------
   Media queries - Display medio-grande (≤1300px)
------------------------------------------------------------------ */
@media (max-width: 1300px) {
  .activity-header {
    .activity-eyebrow {
      font-size: $font-eyebrow-lg;
      
      &::before {
        height: 10px;
        left: -25px;
      }
    }

    .activity-title {
      font-size: $font-title-lg;
      font-weight: 300;
    }
  }
}

/* ------------------------------------------------------------------
   Tablet / Mobile (≤1024px) — padding laterale con !important
   necessario perché body main { padding: 0 !important } nel global
   sovrascrive il padding del componente.
------------------------------------------------------------------ */
@media (max-width: 1024px) {
  .activity-detail {
    padding-left: $space-1 !important;
    padding-right: $space-1 !important;
    overflow-x: hidden;
  }

  /* Immagine a tutta larghezza (bleed) — bilancia esattamente il padding */
  .activity-image-carousel {
    margin-left: -$space-1;
    margin-right: -$space-1;
    width: calc(100% + 2rem);
  }

  .carousel-image {
    width: 100%;
    max-height: 300px;
    box-shadow: none;
  }

  .carousel-arrow {
    &.carousel-arrow-left { left: 0.75rem; }
    &.carousel-arrow-right { right: 0.75rem; }
  }
}

/* ------------------------------------------------------------------
   Media queries - Tablet (≤920px)
------------------------------------------------------------------ */
@media (max-width: 920px) {
  .activity-detail {
    padding-left: $space-1 !important;
    padding-right: $space-1 !important;
  }

  .activity-header {
    .activity-eyebrow {
      font-size: $font-eyebrow-sm;
      margin-bottom: $space-1;
      white-space: normal;

      &::before {
        height: 8px;
        left: -20px;
      }
    }

    .activity-title {
      font-size: $font-title-sm;
      font-weight: 300;
    }
  }

  .carousel-arrow {
    width: 50px;
    height: 50px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
}

/* ------------------------------------------------------------------
   Mobile (≤ 768 px)
------------------------------------------------------------------ */
@media (max-width: 768px) {
  .activity-detail {
    padding-left: $space-1 !important;
    padding-right: $space-1 !important;
  }
}

/* ------------------------------------------------------------------
   Mobile (≤480px)
------------------------------------------------------------------ */
@media (max-width: 480px) {
  .activity-detail {
    padding-left: $space-1 !important;
    padding-right: $space-1 !important;
  }

  .activity-header {
    .activity-eyebrow {
      font-size: $font-eyebrow-xs;
      margin-bottom: $space-0-5;

      &::before {
        height: 7px;
        left: -15px;
      }
    }

    .activity-title {
      font-size: $font-title-xs;
      font-weight: 300;
    }
  }

  .activity-image-carousel {
    margin-bottom: $space-1-5;
  }
}

/* ------------------------------------------------------------------
   Carosello Immagini
------------------------------------------------------------------ */
.activity-image-carousel {
  width: 100%;
  margin: 0 auto $space-3 auto;
  text-align: center;
}

.carousel-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  @include flex-gap(1rem);
  width: 100%;
}

.carousel-image-wrapper,
.single-image-wrapper {
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.carousel-image {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 500px;
  display: inline-block;
  box-shadow: 0 4px 16px rgba($clr-brand-dark, 0.1);
  transition: all 0.3s ease;

  @media (min-width: 1025px) and (max-width: 1300px) {
    max-height: 360px;
  }

  @media (hover: hover) {
    &:hover {
      transform: scale(1.02);
      box-shadow: 0 8px 24px rgba($clr-brand-dark, 0.15);
    }
  }
}

.carousel-arrow {
  position: absolute;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba($clr-brand-dark, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
  flex-shrink: 0;
  outline: none;
  padding: 0;

  svg {
    width: 24px;
    height: 24px;
    color: $clr-brand-dark;
  }

  @media (hover: hover) {
    &:hover {
      background-color: rgba(255, 255, 255, 1);
      transform: scale(1.1);
      box-shadow: 0 4px 12px rgba($clr-brand-dark, 0.15);
    }
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
  }

  /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
     scartata per intero. Si ripiega su :focus, che mostra l'anello
     anche col mouse: e' il degrado accettato. */
  .no-focusvisible &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }

  &.carousel-arrow-left {
    left: -3rem;
  }

  &.carousel-arrow-right {
    right: -3rem;
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    padding: 0;

    svg {
      width: 20px;
      height: 20px;
    }

    &.carousel-arrow-left {
      left: 0.75rem;
    }

    &.carousel-arrow-right {
      right: 0.75rem;
    }

    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }

    /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
       scartata per intero. Si ripiega su :focus, che mostra l'anello
       anche col mouse: e' il degrado accettato. */
    .no-focusvisible &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    padding: 0;

    svg {
      width: 18px;
      height: 18px;
    }

    &.carousel-arrow-left {
      left: 0.5rem;
    }

    &.carousel-arrow-right {
      right: 0.5rem;
    }

    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }

    /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
       scartata per intero. Si ripiega su :focus, che mostra l'anello
       anche col mouse: e' il degrado accettato. */
    .no-focusvisible &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }
  }
}

.carousel-dots {
  display: flex;
  justify-content: center;
  @include flex-gap(0.75rem);
  margin-top: 1.5rem;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background-color: rgba($clr-text-light, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;

  @media (hover: hover) {
    &:hover {
      background-color: rgba($clr-text-light, 0.5);
      transform: scale(1.2);
    }
  }

  &.active {
    background-color: $clr-tertiary;
    width: 24px;
    border-radius: 5px;
  }
}

/* ------------------------------------------------------------------
   Libro consultabile (PDF)
   Qui si decide solo la LARGHEZZA. L'altezza la calcola il lettore dal
   foglio (`fit-sheet`): le altezze fisse di prima (600-700px, copiate da
   opere d'arte) tagliavano in basso le pagine verticali e lasciavano
   vuoti sotto quelle orizzontali. (2026-09-16)
------------------------------------------------------------------ */
.activity-pdf {
  width: 100%;
  max-width: 750px;
  margin: $space-2 auto 0;

  :deep(.pdf-viewer-container) {
    border-radius: 0;
  }

  @media (max-width: 1300px) {
    max-width: 600px;
  }

  /* Il lettore sta dentro .html-text-section > .container, che ha un suo padding
     laterale oltre a quello della pagina: senza compensarlo, sotto i ~630px il
     lettore perdeva fino a 32px di larghezza e la barra andava a capo.
     Margine = il meno negativo fra "rientra nel padding" e "centra i 600px":
     stretto recupera il padding, largo resta 600px centrato come prima.
     I valori sono quelli di SectionHtmlText (≤768: $space-1, ≤360: $space-0-75). */
  @media (max-width: 768px) {
    margin-left: max(-#{$space-1}, calc((100% - 600px) / 2));
    margin-right: max(-#{$space-1}, calc((100% - 600px) / 2));
    width: auto;
  }

  @media (max-width: 480px) {
    margin-top: $space-0-5;
  }

  @media (max-width: 360px) {
    margin-left: max(-#{$space-0-75}, calc((100% - 600px) / 2));
    margin-right: max(-#{$space-0-75}, calc((100% - 600px) / 2));
  }
}

/* ──────────────────────────────────────────────────────────────
   L'eyebrow aveva un padding-left che spostava il TESTO a destra
   lasciando il box dov'era: era quello a disallinearlo dal titolo.
   Qui viene azzerato, cosi' l'eyebrow parte esattamente dove parte
   il titolo. Nessun titolo viene spostato.
   Il ::before e' position:absolute e non dipende dal padding.
   Classe ripetuta tre volte per battere le regole annidate originali.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .activity-eyebrow.activity-eyebrow.activity-eyebrow {
    padding-left: 0;
  }
}

/* ──────────────────────────────────────────────────────────────
   ALLINEAMENTO AL PARAGRAFO
   eyebrow, titolo e corpo del testo devono partire dalla stessa x,
   quella di "chi siamo": 44px a 390, 39 a 360, 54 a 600, 102 a 768-920,
   122 a 1024. Valori ricavati misurando il primo glifo di ogni elemento.
   Classe ripetuta tre volte per battere le regole annidate originali;
   blocchi in fondo al file perche' l'ordine decide fra breakpoint.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .activity-eyebrow.activity-eyebrow.activity-eyebrow {
    padding-left: 0;
  }
}

@media (max-width: 480px) {
  .activity-eyebrow.activity-eyebrow.activity-eyebrow {
    margin-left: 28px;
    padding-left: 0;
  }

  .activity-title.activity-title.activity-title {
    margin-left: 28px;
  }

  :deep(.html-content) {
    margin-left: 12px;
  }
}

@media (max-width: 360px) {
  .activity-eyebrow.activity-eyebrow.activity-eyebrow {
    margin-left: 23px;
    padding-left: 0;
  }

  .activity-title.activity-title.activity-title {
    margin-left: 23px;
  }

  :deep(.html-content) {
    margin-left: 11px;
  }
}
</style>
